import { useEffect, useRef, type ReactNode } from 'react';
import type { JsonValue } from '@inf-monkeys-tech/monkeys';
import { BaseCheckbox } from '../base/BaseCheckbox';

export interface RecordEditorNullToggleProps {
  value: JsonValue;
  label: string;
  checkedWhen?: 'null' | 'non-null';
  retainSelectionOnNull?: boolean;
  disabled: boolean;
  resetKey: string;
  onChange(value: JsonValue): void;
  children: ReactNode;
}
const stringIds = (value: JsonValue): value is string[] => Array.isArray(value) && value.every(id => typeof id === 'string');

/** Local selection memory belongs only to this field's current draft scope. */
export function RecordEditorNullToggle({ value, label, checkedWhen = 'null', retainSelectionOnNull = false, disabled, resetKey, onChange, children }: RecordEditorNullToggleProps) {
  const active = useRef(true);
  useEffect(() => { active.current = true; return () => { active.current = false; }; }, []);
  const memory = useRef<{ key: string; ids?: string[] }>({ key: resetKey });
  const current = useRef({ resetKey, disabled, onChange });
  current.current = { resetKey, disabled, onChange };
  if (memory.current.key !== resetKey) memory.current = { key: resetKey };
  if (!retainSelectionOnNull) memory.current.ids = undefined;
  else if (stringIds(value)) memory.current.ids = [...value];
  return <div>
    <label className="flex items-center gap-2">
      <BaseCheckbox checked={checkedWhen === 'null' ? value === null : value !== null} disabled={disabled} onChange={event => {
        if (!active.current || current.current.resetKey !== resetKey || current.current.disabled) return;
        const nextNull = checkedWhen === 'null' ? event.currentTarget.checked : !event.currentTarget.checked;
        const restored = retainSelectionOnNull && memory.current.ids ? [...memory.current.ids] : [];
        current.current.onChange(nextNull ? null : restored);
      }} />
      <span>{label}</span>
    </label>
    {value === null ? null : children}
  </div>;
}
